import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Radio,
  MenuItem,
  Select,
  TextareaAutosize,
  Typography,
  TablePagination,
  TextField,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelIcon from '@mui/icons-material/Cancel';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { RadioButtonChecked, RadioButtonUnchecked } from "@mui/icons-material";
import Papa from "papaparse";
import "../../css/OutPutDirectory.css";
import { setCsvData, updateCsvData, updateUniqueField, deleteCsvRow } from '../../redux/slices/CSVDataSlice';
import { useLocation } from "react-router-dom";
import { toast } from 'react-toastify';

const CsvFileScreen = () => {
  const fileInputRef = useRef(null);
  const {csvData} = useSelector((state) => state.CSVDataSlice);
  
  const dispatch = useDispatch();
  const location = useLocation();
  const [newRowData, setNewRowData] = useState({
    Unique: '',
    Checksum: '',
    Keys: '',
    Description: '',
    Format: 'String',
    Fields: '',
    Fields_Description: ''
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddDataDialog, setOpenAddDataDialog] = useState(false);
  const [openAddSubfieldsDialog, setOpenAddSubfieldsDialog] = useState(false);
  const [dialogData, setDialogData] = useState([]);
  const [dialogRowIndex, setDialogRowIndex] = useState(null);
  const [editingRowIndex, setEditingRowIndex] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchText, setSearchText] = useState('');
  const [rows, setRows] = useState([]);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  const csvHeaders = {
    Unique: "Unique identifier for the record",
    Checksum: "Checksum value for data integrity",
    Keys: "Output key used for reference",
    Description: "Details about the key",
    Format: "Expected format (e.g., string, integer, date)",
    Actions: "Edit & Delete a row"
  };

  const formatOptions = ["String", "Boolean", "Enum", "Nest"];

  const getCellStyle = (index) => {
    switch (index) {
      case 0:
        return { width: 100, fontSize: '1rem', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 1:
        return { width: 110, fontSize: '1rem', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 2:
        return { width: 200, fontSize: '1rem', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 3:
        return { width: 500, fontSize: '1rem', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 4:
        return { width: 100, fontSize: '1rem', fontFamily: "'Poppins', sans-serif", color: 'white' };
      case 5:
        return { width: 100, fontSize: '1rem', fontFamily: "'Poppins', sans-serif", color: 'white' };
      default:
        return { padding: "8px" };
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: function (results) {
          dispatch(setCsvData(results.data));
        },
      });
    } else {
      alert('Please upload a valid CSV file');
    }
  };

  const handleExportCSV = () => {
    const defaultHeaders = [
      "Unique",
      "Checksum",
      "Keys",
      "Description",
      "Format",
      "Fields",
      "Fields_Description"
    ];
 
    const dataToExport = csvData && csvData.length > 0
      ? csvData
      : [Object.fromEntries(defaultHeaders.map((header) => [header, ""]))];
 
    const csv = Papa.unparse(dataToExport, {
      columns: defaultHeaders
    });
 
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
 
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'keys.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDragAndDrop = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleOpenDialog = (row, index) => {
    const fieldsArray = row.Fields.split(',');
    const descriptionsArray = row.Fields_Description.split(',');
    const dialogData = fieldsArray.map((field, i) => ({
      field: field.trim(),
      description: descriptionsArray[i]?.trim() || ''
    }));
    setDialogData(dialogData);
    setDialogRowIndex(index);
    setOpenDialog(true);
  };

  const handleAddRow = () => {
    setRows([...rows, { field: '', description: '' }]);
  };

  const handleRemoveRow = (index) => {
    const updatedRows = [...rows];
    updatedRows.splice(index, 1);
    setRows(updatedRows);
  };

  const handleSaveNewSubFields = () => {
    const fields = rows.map((r) => r.field).join(',');
    const fieldsDescription = rows.map((r) => r.description).join(',');

    setNewRowData((prev) => ({
      ...prev,
      Fields: fields,
      Fields_Description: fieldsDescription,
    }));

    setOpenAddSubfieldsDialog(false);
  };

  const handleSaveFields = () => {
    const cleanedData = dialogData.filter(
      (d) => d.field.trim() !== '' || d.description.trim() !== ''
    );

    const updatedFields = cleanedData.map((d) => d.field.trim()).join(', ');
    const updatedDescriptions = cleanedData.map((d) => d.description.trim()).join(', ');

    dispatch(updateCsvData({
      index: dialogRowIndex,
      key: 'Fields',
      value: updatedFields,
    }));

    dispatch(updateCsvData({
      index: dialogRowIndex,
      key: 'Fields_Description',
      value: updatedDescriptions,
    }));

    setOpenDialog(false);
    setEditingRowIndex(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setEditingRowIndex(null);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = () => {
    if (deleteIndex) {
      dispatch(deleteCsvRow(deleteIndex));
      if (editingRowIndex === deleteIndex) setEditingRowIndex(null);
    }
    toast.success('Record Removed Successfully');
    setOpenConfirmDialog(false);
    setDeleteIndex(null);
    setPage(0);
  };

  const filteredData = csvData
    .map((row, i) => ({ ...row, originalIndex: i }))
    .filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchText.toLowerCase())
      )
    );


  return (
    <Box>
      {/* Upload Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', ml: 45 }}>
        <Button
          variant="contained"
          startIcon={<UploadFileIcon />}
          onClick={handleDragAndDrop}
          sx={{
            backgroundColor: '#1E3899',
            textTransform: 'none',
            fontFamily: "'Poppins', sans-serif",
            fontSize: '1rem',
            padding: '10px 20px',
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#162d73',
            },
          }}
        >
          Upload CSV
        </Button>

        <input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          ref={fileInputRef}
        />

        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={handleExportCSV}
          sx={{
            backgroundColor: '#1E3899',
            textTransform: 'none',
            fontFamily: "'Poppins', sans-serif",
            fontSize: '1rem',
            padding: '10px 20px',
            ml: 3,
            borderRadius: '6px',
            '&:hover': {
              backgroundColor: '#162d73',
            },
          }}
        >
          Download CSV
        </Button>
      </Box>
        <Box sx={{ mr: 5 }}>
          <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%" gap={2} mb={1}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search…"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setEditingRowIndex(null);
              }}
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '1rem',
                backgroundColor: 'white',
                borderRadius: '6px',
                width: '250px',
              }}
            />

            <Button
              variant="contained"
              onClick={() => {
                setNewRowData({
                  Unique: '',
                  Checksum: '',
                  keys: '',
                  Description: '',
                  Format: "String",
                  Fields: '',
                  Fields_Description: ''
                });
                setRows([]);
                setEditingRowIndex(null);
                setOpenAddDataDialog(true);
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                textTransform: 'none',
                fontFamily: "'Poppins', sans-serif",
                fontSize: '1rem',
                padding: '4px 14px 8px 7px',
                borderRadius: '6px',
                gap: 1,
              }}
            >
              <AddCircleOutlineIcon sx={{ color: 'white' }} />
              <Typography sx={{ color: 'white', fontWeight: 500 }}>Add</Typography>
            </Button>
          </Box>

          <TableContainer component={Paper} className="table-container" width="100%">
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1976D2" }}>
                  {Object.keys(csvHeaders)
                    .filter((col) => col !== "Fields" && col !== "Fields_Description")
                    .map((col, i) => (
                      <TableCell
                        key={col}
                        sx={{
                          ...getCellStyle(i),
                          pl: 3,
                        }}
                      >
                        {col}
                        <Tooltip title={csvHeaders[col]} arrow>
                          <InfoOutlinedIcon
                            fontSize="small"
                            sx={{ verticalAlign: "middle", marginLeft: "4px" }}
                          />
                        </Tooltip>
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Radio
                        checked={Number(row.Unique) === 1}
                        onChange={() =>
                          dispatch(updateUniqueField({
                            index: row.originalIndex,
                            key: 'Unique'
                          }))
                        }
                        disabled={(editingRowIndex !== page * rowsPerPage + index) || row['Format'] !== 'String'}
                      />
                    </TableCell>
                    <TableCell>
                      <Checkbox
                        checked={Number(row.Checksum) === 1}
                        onChange={(e) =>
                          dispatch(updateCsvData({
                            index: row.originalIndex,
                            key: 'Checksum',
                            value:
                              e.target.checked ? '1' : ''
                          }))
                        }
                        disabled={(editingRowIndex !== page * rowsPerPage + index) || row['Format'] !== 'String'}
                      />
                    </TableCell>
                    <TableCell>
                      {editingRowIndex === page * rowsPerPage + index ? (
                        <TextareaAutosize
                          maxRows={1}
                          value={row['Keys']}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            dispatch(updateCsvData({ index: row.originalIndex, key: 'Keys', value: newValue }))
                          }}
                          style={{
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            resize: "vertical",
                            width: "100%",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" style={{ padding: "8px" }}>
                          {row['Keys']}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 500 }}>
                      {editingRowIndex === page * rowsPerPage + index ? (
                        <TextareaAutosize
                          maxRows={3}
                          value={row['Description']}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            dispatch(updateCsvData({ index: row.originalIndex, key: 'Description', value: newValue }))
                          }}
                          style={{
                            padding: "8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            resize: "vertical",
                            width: "100%",
                          }}
                        />
                      ) : (
                        <Typography variant="body2" style={{ padding: "8px", whiteSpace: "pre-wrap" }}>
                          {row['Description']}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 250 }}>
                      <>
                        <Box display="flex" alignItems="center">
                          <Select
                            value={row['Format'] || ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              dispatch(updateCsvData({ index: row.originalIndex, key: 'Format', value: newValue }));
                              if (newValue !== 'String') {
                                dispatch(updateCsvData({ index: row.originalIndex, key: 'Checksum', value: '' }));
                                dispatch(updateCsvData({ index: row.originalIndex, key: 'Unique', value: '' }));
                              }
                            }}
                            variant="outlined"
                            size="small"
                            style={{
                              minWidth: 120,
                              height: 30,
                              borderRadius: 25,
                              fontSize: 12,
                            }}
                            disabled={editingRowIndex !== page * rowsPerPage + index}
                          >
                            {formatOptions.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                          {row['Format'] === "Nest" && (
                            <Button
                              onClick={() => handleOpenDialog(row, page * rowsPerPage + index)}
                              variant="contained"
                              size="small"
                              style={{
                                marginLeft: "8px",
                                backgroundColor: editingRowIndex !== page * rowsPerPage + index ? "grey" : "#1976D2",
                                color: "white",
                                height: 25,
                                borderRadius: 25,
                                minWidth: 80,
                              }}
                              disabled={editingRowIndex !== page * rowsPerPage + index}
                            >
                              <p style={{ fontSize: 9, margin: 0 }}>Subfields</p>
                            </Button>
                          )}
                        </Box>
                      </>
                    </TableCell>
                    <TableCell>
                      <IconButton onClick={() => setEditingRowIndex(page * rowsPerPage + index)}>
                        <EditIcon style={{ color: "blue" }} />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setDeleteIndex(page * rowsPerPage + index);
                          setOpenConfirmDialog(true);
                        }}
                      >
                        <DeleteIcon style={{ color: "darkred" }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={csvData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
    

      {/* Popup Dialog for Subfields */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        sx={{ "& .MuiDialog-paper": { width: "600px", maxWidth: "90%" } }}
      >
        <DialogTitle>
          Field Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CancelIcon />
          </IconButton>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveFields}
            sx={{
              position: "absolute",
              right: 50,
              top: 15,
              textTransform: "none",
            }}
          >
            Save
          </Button>
        </DialogTitle>

        <DialogContent>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976D2" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: '40%' }}>
                  <b>Fields</b>
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: '60%' }}>
                  <b>Field Description</b>
                </TableCell>
                <TableCell sx={{ width: '40px' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {dialogData.map((field, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <TextareaAutosize
                      maxRows={1}
                      value={field.field}
                      onChange={(e) => {
                        const newData = [...dialogData];
                        newData[index].field = e.target.value;
                        setDialogData(newData);
                      }}
                      style={{
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "14px",
                        resize: "vertical",
                        width: "100%",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextareaAutosize
                      maxRows={3}
                      value={field.description}
                      onChange={(e) => {
                        const newData = [...dialogData];
                        newData[index].description = e.target.value;
                        setDialogData(newData);
                      }}
                      style={{
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "14px",
                        resize: "vertical",
                        width: "100%",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => {
                        const newData = dialogData.filter((_, i) => i !== index);
                        setDialogData(newData);
                      }}
                    >
                      <RemoveCircleOutlineIcon style={{ color: "darkred" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Add Row Button */}
          <Button
            variant="outlined"
            sx={{ mt: 2, textTransform: "none" }}
            onClick={() =>
              setDialogData((prev) => [...prev, { field: "", description: "" }])
            }
          >
            + Add Row
          </Button>
        </DialogContent>
      </Dialog>

      {/* Popup Dialog for Add Data */}
      <Dialog
        open={openAddDataDialog}
        onClose={() => setOpenAddDataDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: '1000px',
          },
        }}
      >
        <IconButton
          aria-label="close"
          onClick={() => setOpenAddDataDialog(false)}
          sx={{
            position: 'absolute',
            right: 1,
            top: 1,
            color: '#555',
            zIndex: 2,
          }}
        >
          <CancelIcon />
        </IconButton>
        <DialogContent
          sx={{
            pt: 6,
            position: 'relative',
          }}
        >
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#1976D2" }}>
                  {Object.keys(csvHeaders)
                    .filter((col) => col !== "Fields" && col !== "Fields_Description" && col !== "Actions")
                    .map((col, i) => (
                      <TableCell
                        key={col}
                        sx={{
                          ...getCellStyle(i),
                          pl: 3,
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Checkbox
                      icon={<RadioButtonUnchecked />}
                      checkedIcon={<RadioButtonChecked />}
                      checked={Number(newRowData['Unique'] === 1)}
                      onChange={() => {
                        const isCurrentlySelected = Number(newRowData['Unique'] === 1);

                        if (!isCurrentlySelected) {
                          const alreadyHasUnique = csvData.some((row) => row['Unique'] === '1');
                          if (alreadyHasUnique) {
                            toast.warning('Unique field already exists');
                            return;
                          }
                        }

                        setNewRowData((prev) => ({
                          ...prev,
                          ['Unique']: isCurrentlySelected ? '' : '1',
                        }));
                      }}
                      disabled={newRowData['Format'] !== 'String'}
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={Number(newRowData['Checksum']) === 1}
                      onChange={(e) =>
                        setNewRowData((prev) => ({
                          ...prev,
                          ['Checksum']: e.target.checked ? '1' : '',
                        }))
                      }
                      disabled={newRowData['Format'] !== 'String'}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={newRowData['Keys'] || ''}
                      onChange={(e) =>
                        setNewRowData((prev) => ({
                          ...prev,
                          ['Keys']: e.target.value,
                        }))
                      }
                      inputProps={{ style: { minWidth: '180px' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 500 }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={newRowData['Description'] || ''}
                      onChange={(e) =>
                        setNewRowData((prev) => ({
                          ...prev,
                          ['Description']: e.target.value,
                        }))
                      }
                      inputProps={{ style: { minWidth: '180px' } }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: 250 }}>
                    <>
                      <Box display="flex" alignItems="center">
                        <Select
                          value={newRowData['Format'] || ''}
                          onChange={(e) => {
                            const newFormat = e.target.value;
                            setNewRowData((prev) => ({
                              ...prev,
                              Format: newFormat,
                              Checksum: newFormat !== 'String' ? '' : prev.Checksum,
                              Unique: newFormat !== 'String' ? '' : prev.Unique,
                            }));
                          }}
                          variant="outlined"
                          size="small"
                          style={{
                            minWidth: 120,
                            height: 30,
                            borderRadius: 25,
                            fontSize: 12,
                          }}
                        >
                          {formatOptions.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                        {newRowData['Format'] === "Nest" && (
                          <Button
                            onClick={() => setOpenAddSubfieldsDialog(true)}
                            variant="contained"
                            size="small"
                            style={{
                              marginLeft: "8px",
                              backgroundColor: "#1976D2",
                              color: "white",
                              height: 25,
                              borderRadius: 25,
                              minWidth: 80,
                            }}
                          >
                            <p style={{ fontSize: 9, margin: 0 }}>Subfields</p>
                          </Button>
                        )}
                      </Box>
                    </>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
            }}
            onClick={() => {
              const isUniqueSelected = newRowData['Unique'] === '1';
              const alreadyHasUnique = csvData.some((row) => row['Unique'] === '1');

              if (isUniqueSelected && alreadyHasUnique) {
                alert("Only one row can be marked as Unique.");
                return;
              }

              dispatch(setCsvData([...csvData, newRowData]));
              setOpenAddDataDialog(false);

              const newIndex = csvData.length;
              const newPage = Math.floor(newIndex / rowsPerPage);
              setPage(newPage);
              toast.success('Record Added Successfully');
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Popup Dialog for Add Subfields */}
      <Dialog
        open={openAddSubfieldsDialog}
        onClose={() => setOpenAddSubfieldsDialog(false)}
        sx={{ "& .MuiDialog-paper": { width: "600px", maxWidth: "90%" } }}
      >
        <DialogTitle>
          Field Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenAddSubfieldsDialog(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CancelIcon />
          </IconButton>
          <Button
            variant="contained"
            size="small"
            onClick={handleSaveNewSubFields}
            sx={{
              position: "absolute",
              right: 50,
              top: 15,
              textTransform: "none",
            }}
          >
            Save
          </Button>
        </DialogTitle>

        <DialogContent>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#1976D2" }}>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: '40%' }}>
                  <b>Fields</b>
                </TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold", width: '60%' }}>
                  <b>Field Description</b>
                </TableCell>
                <TableCell sx={{ width: '40px' }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={index}>
                  <TableCell sx={{ width: '40%' }}>
                    <TextareaAutosize
                      maxRows={1}
                      value={row.field}
                      onChange={(e) => {
                        const updatedRows = [...rows];
                        updatedRows[index].field = e.target.value;
                        setRows(updatedRows);
                      }}
                      style={{
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "14px",
                        resize: "vertical",
                        width: "100%",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '60%' }}>
                    <TextareaAutosize
                      maxRows={3}
                      value={row.description}
                      onChange={(e) => {
                        const updatedRows = [...rows];
                        updatedRows[index].description = e.target.value;
                        setRows(updatedRows);
                      }}
                      style={{
                        padding: "8px",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        fontSize: "14px",
                        resize: "vertical",
                        width: "100%",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleRemoveRow(index)}>
                      <RemoveCircleOutlineIcon style={{ color: "darkred" }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              sx={{ textTransform: 'none' }}
            >
              Add Row
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Popup Dialog for Delete Confirmation Box */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this record? This process cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CsvFileScreen;
